import { useEffect, useState, useRef } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";
import icWarningTriangle from "@/assets/icons/ic-warning-triangle.svg";
import { Button } from "@/components/ui/button";
import { SpinnerOverlay } from "@/components/ui/spinner-overlay";
import { ConfirmDialog } from "@/components/common";
import { CHECKLIST } from "@/components/project-checklist/const";
import { ChecklistSection } from "@/components/project-checklist/checklist-section";
import { generateRfp, type SectionScores } from "@/services/chat-service";
import { STORAGE_KEY } from "../chat/chat-page";

// Maps checklist section indices to SectionScores keys (in order)
const SECTION_SCORE_KEYS: (keyof SectionScores)[] = [
  "데이터_준비도",
  "업무_정의_수준",
  "시스템_연동_가능성",
  "보안_규제_적합성",
  "Agent_운영통제",
  "조직_의사결정_준비도",
  "PoC_실행_가능성",
  "운영_확산_가능성",
];

interface RfpGenerationData {
  projectName: string;
  messages: Array<{ role: "user" | "assistant"; content: string }>;
}

export const ProjectReportChecklist = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Record<string, number | null>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);

  // Track intentional navigation to avoid blocking
  const isNavigatingRef = useRef(false);

  // Check if user has answered at least one question
  // const hasAnswers = Object.keys(answers).length > 0;

  // Block navigation if user has answered questions (except for chat page)
  // const shouldBlock = hasAnswers;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      // shouldBlock &&
      currentLocation.pathname !== nextLocation.pathname &&
      !nextLocation.pathname.includes("/chat"),
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (isNavigatingRef.current) {
        blocker.proceed?.();
      } else {
        setShowExitDialog(true);
      }
    }
  }, [blocker]);

  const handleExitConfirm = () => {
    setShowExitDialog(false);
    sessionStorage.removeItem(STORAGE_KEY);
    isNavigatingRef.current = true;
    blocker.proceed?.();
  };

  const handleExitCancel = () => {
    setShowExitDialog(false);
    isNavigatingRef.current = false;
    blocker.reset?.();
  };

  const isAllAnswered = CHECKLIST.every((_, sectionIndex) => answers[sectionIndex] != null);

  const handleSelect = (sectionIndex: number, value: number) => {
    setAnswers((prev) => ({
      ...prev,
      [sectionIndex]: value,
    }));
  };

  const handleGenerate = async () => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      console.error("No RFP generation data found");
      return;
    }

    const data: RfpGenerationData = JSON.parse(stored);

    // Build section_scores from answers
    const section_scores: SectionScores = {} as SectionScores;
    SECTION_SCORE_KEYS.forEach((key, index) => {
      section_scores[key] = answers[index] ?? 1;
    });

    setIsGenerating(true);

    try {
      const response = await generateRfp({
        project_name: data.projectName,
        chat_history: data.messages.map((m) => ({ role: m.role, content: m.content })),
        section_scores,
      });

      const responseData = response.data.data;

      // Clear stored data
      sessionStorage.removeItem(STORAGE_KEY);

      // Navigate to report page with replace: true so checklist entry is replaced in history
      isNavigatingRef.current = true;
      navigate(`/projects/${responseData.project_id}/report`, { replace: true });
    } catch (error) {
      console.error("Failed to generate RFP:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <div className="lg:max-w-[1120px] px-5 min-[1120px]:px-0 px-5  mx-auto mt-[24px] mb-[40px] lg:mb-[100px]">
        <div className="flex flex-col gap-[16px] lg:flex-row justify-between items-center">
          <div className="flex flex-col lg:flex-row lg:items-center gap-[2px]">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                isNavigatingRef.current = true;
                navigate(-1);
              }}
              className="w-10 h-10 lg:w-12 lg:h-12"
              aria-label="뒤로가기"
            >
              <img src={icArrowLeft} alt="Icon Arrow Left" />
            </Button>
            <div className="flex flex-col gap-[4px]">
              <h3 className="text-[20px] font-bold text-[#000000]">
                AI Agent 도입 Readiness Check List
              </h3>
              <p className="text-[#000000] text-[14px]">
                AI 에이전트를 도입하기 전, 어떤 리스크가 있고, 무엇을 먼저 해결해야 하는가를 팀
                전체가 정렬된 시각으로 바라볼 수 있도록 돕는 진단 프레임워크입니다.
              </p>
            </div>
          </div>
        </div>

        <div className="block w-full h-px bg-black/[0.09] my-[24px]" />

        <div className="flex flex-col gap-8 lg:gap-12">
          {CHECKLIST.map((item, sectionIndex) => (
            <ChecklistSection
              key={sectionIndex}
              item={item}
              selectedValue={answers[sectionIndex] ?? null}
              onSelect={(value) => handleSelect(sectionIndex, value)}
            />
          ))}
        </div>
        <div className="flex justify-center mt-[24px]">
          <Button
            variant={"default"}
            disabled={!isAllAnswered || isGenerating}
            onClick={handleGenerate}
            className="w-full lg:w-[292px] h-[48px] rounded-[4px] text-[16px] leading-[24px] py-[12px] px-[20px] tracking-[-0.01em]"
          >
            추천 에이전트 및 AX 컨설팅 결과 확인
          </Button>
        </div>

        {isGenerating && <SpinnerOverlay message="Generating your report..." />}

        <ConfirmDialog
          open={showExitDialog}
          onOpenChange={(open) => {
            if (!open) handleExitCancel();
          }}
          iconVariant="warning"
          icon={<img src={icWarningTriangle} alt="" width={32} height={32} />}
          title="프로젝트 내용 삭제 확인"
          description="현재 논의 중인 프로젝트와 관련된 내용이 삭제됩니다. 계속 진행하시겠습니까?"
          primaryAction={{ label: "네", onClick: handleExitConfirm }}
          secondaryAction={{ label: "아니요", onClick: handleExitCancel }}
        />
      </div>
    </>
  );
};

export default ProjectReportChecklist;
